import type { ReactElement } from 'react';
import type {
  AgentmeshScanCoverage,
  ExtensionRiskEstimate,
} from '@kirin/api-client/contracts/extensions';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { RiskEstimateCell } from '@/components/shared/extensions/RiskEstimateCell';
import { ScanStatusCell } from '@/components/shared/extensions/ScanStatusCell';
import {
  isNotApplicable,
  marketplaceLabel,
} from '@/components/shared/extensions/agentmesh-risk-labels';

interface AgentmeshRiskDetailsProps {
  riskEstimate: ExtensionRiskEstimate | undefined;
  /** The extension's Scan status rollup (KIR-5208). */
  scanStatus?: AgentmeshScanCoverage | null;
  /** A built-in without an AgentMesh verdict reads Not applicable (KIR-4830). */
  isBuiltIn?: boolean;
}

/**
 * Expanded-row section showing the full AgentMesh assessment for an
 * extension (KIR-1928): its Scan status and Risk (KIR-5208), assessment
 * reasons, and code evidence snippets. Explains the empty state when
 * AgentMesh has no verdict for the extension.
 */
export function AgentmeshRiskDetails({
  riskEstimate,
  scanStatus,
  isBuiltIn = false,
}: AgentmeshRiskDetailsProps): ReactElement {
  const hasVerdict = (riskEstimate?.riskBucket ?? null) !== null;
  const notApplicable = !hasVerdict && isNotApplicable(scanStatus, isBuiltIn);
  const marketplace = marketplaceLabel(riskEstimate?.marketplace);

  return (
    <div className="space-y-3 px-4 py-3" data-testid="agentmesh-risk-details">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold">Risk Estimate</h4>
        {/* The paragraph below explains a state without a verdict. */}
        <span className="text-xs text-muted-foreground">Scan status</span>
        <ScanStatusCell
          scanStatus={scanStatus}
          isBuiltIn={isBuiltIn}
          describe={false}
        />
        <span className="text-xs text-muted-foreground">Risk</span>
        <RiskEstimateCell
          riskEstimate={riskEstimate}
          notApplicable={notApplicable}
          describe={false}
        />
        {riskEstimate && hasVerdict ? (
          <span className="text-xs text-muted-foreground">
            Assessed <RelativeTimeWithTooltip date={riskEstimate.checkedAt} />
          </span>
        ) : null}
        {hasVerdict && marketplace ? (
          // AgentMesh can hold the version in several marketplaces; name the
          // one this verdict is from.
          <span className="text-xs text-muted-foreground">
            Marketplace: {marketplace}
          </span>
        ) : null}
        {riskEstimate?.agentmeshUrl ? (
          <a
            href={riskEstimate.agentmeshUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline"
          >
            View in AgentMesh ↗
          </a>
        ) : null}
      </div>

      {notApplicable ? (
        <p className="text-sm text-muted-foreground">
          This extension ships inside the IDE, and AgentMesh has no verdict for
          it.
        </p>
      ) : !hasVerdict ? (
        <p className="text-sm text-muted-foreground">
          No AgentMesh assessment is available for this extension yet.
        </p>
      ) : (
        <>
          {riskEstimate && riskEstimate.reasons.length > 0 ? (
            <div>
              <h5 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Assessment
              </h5>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {riskEstimate.reasons.map((reason, idx) => (
                  <li key={`${idx}-${reason}`}>{reason}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {riskEstimate && riskEstimate.evidence.length > 0 ? (
            <div>
              <h5 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Evidence
              </h5>
              <div className="space-y-2">
                {riskEstimate.evidence.map((item, idx) => (
                  <div key={`${idx}-${item.filePath}`}>
                    <div className="text-xs text-muted-foreground">
                      {item.filePath}
                    </div>
                    <pre className="mt-0.5 overflow-x-auto rounded bg-muted p-2 text-xs">
                      {item.snippet}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

AgentmeshRiskDetails.displayName = 'AgentmeshRiskDetails';
